import React, { useContext, useEffect, useRef, useState } from 'react';

import { Navigate } from 'react-router';

import styled from '@emotion/styled';

import { Container } from '../../components/Container';
import { Loader } from '../../components/Loader';
import { MainLayout } from '../../components/MainLayout';
import { DebtTeller } from '../../components/ScoreTeller/DebtTeller';
import { useRouter } from '../../hooks';
import { ApiContext, CoreContext } from '../../providers';
import { media } from '../../services/media';
import { addUserData } from '../../state';
import { ErrorPage } from '../ErrorPage';

export const DebtRegistryAuth = () => {
    const { store } = useContext(CoreContext);
    const { Upscore } = useContext(ApiContext);
    const activityTimeoutRef = useRef<any>(null);
    const progressIntervalRef = useRef<any>(null);
    const [iframeUrl, setIframeUrl] = useState<any>(null);
    const [redirecTo, setRedirectTo] = useState('');
    const [errorState, setErrorState] = useState<any>(null);
    const [debtProcessing, setDebtProcessing] = useState(false);
    const debtErrorState = `debt-registry-unauthorized`;
    /**
     * The loader should initally show up because we didn't fetched
     * any data yet. This will be set to `false` once we finish retrieving iframe
     * url.
     */
    const [pageLoader, setPageLoader] = useState(true);

    /**
     * We are parsing url query into variables. Values that we need are `code`
     * and `state`. If anyone would try to access this page without these
     * values we simply show a `debt-registry-unauthorized` error page.
     *
     * @type {any} */
    const { query, navigate } = useRouter();
    const { code, state } = query as any;

    /**
     * In case someone stays on the page for too long we don't want to make
     * infinite calls to our backend.
     *
     * After 5 minutes the user will be redirected back to /source-gjeldsregisteret
     */
    useEffect(() => {
        activityTimeoutRef.current = setTimeout(() => {
            setRedirectTo(`/source-gjeldsregisteret`);
        }, 300000);

        return () => window.clearTimeout(activityTimeoutRef.current);
    }, []);

    useEffect(() => {
        if (!code || !state) {
            setRedirectTo(`/source-gjeldsregisteret`);
        }
    }, []);

    useEffect(() => {
        if (code && state) {
            try {
                Upscore.getSourceDebtregistryIframeurl({ code, state })
                    .then(({ url }) => {
                        setIframeUrl(url);
                        navigate({ search: '' });
                    })
                    .then(() => Upscore.getSourceDebtregistryExtract())
                    .then(() =>
                        Upscore.getFunfacts().then((data) =>
                            store.dispatch(
                                addUserData('funFacts', data || null),
                            ),
                        ),
                    )
                    .then(() => {
                        progressIntervalRef.current = setInterval(async () => {
                            const progress =
                                await Upscore.getSourceDebtregistryProgress();

                            if (
                                progress.status === 'PROCESSING' ||
                                progress.status === 'DONE'
                            ) {
                                setDebtProcessing(true);
                            }

                            if (progress.status === 'DONE') {
                                window.clearTimeout(
                                    progressIntervalRef.current,
                                );
                                setTimeout(() => {
                                    setRedirectTo('/myscore');
                                }, Math.floor(Math.random() * 4000) + 2000);
                            }

                            if (progress.status === 'FAILED') {
                                setErrorState(debtErrorState);
                            }
                        }, 1000);
                    })
                    .then(() => {
                        setPageLoader(false);
                    })
                    .catch((error) => {
                        setErrorState(debtErrorState);
                    });
            } catch (error) {
                setErrorState(debtErrorState);
            }
        }

        return () => window.clearInterval(progressIntervalRef.current);
    }, []);

    if (redirecTo) {
        return <Navigate to={redirecTo} />;
    }

    if (errorState) {
        return <ErrorPage error={errorState} />;
    }

    if (!errorState && pageLoader) {
        return <Loader centered />;
    }

    return (
        <Layout className="DebtRegistryAuth" hideFooter>
            <Container className="DebtRegistryAuth__wrapper" sizeGroup="size-3">
                {debtProcessing ? (
                    <DebtTeller
                        scoreTellerTitle="Henter gjeldsinformasjon"
                        funFactsTitle="Visste du at.."
                        backgroundImage="/assets/images/bank-login-background.png"
                    />
                ) : (
                    <>
                        {iframeUrl && (
                            <DebtRegistryIframe
                                src={iframeUrl}
                                title="Gjeldsregisteret"
                                style={{ width: '100%' }}
                            />
                        )}
                    </>
                )}
            </Container>
        </Layout>
    );
};

export default DebtRegistryAuth;

const Layout = styled(MainLayout)`
    position: relative;
`;

const DebtRegistryIframe = styled.iframe`
    border: none;
    width: 100%;
    z-index: 10;
    min-height: calc(100vh - 87px - 20px);

    ${media.min('md')} {
        min-height: calc(100vh - 124px - 30px);
    }
`;
